import { createSelector } from 'reselect';

const getPropsId = (state, props) => props.id;
const getComponents = state => state.components.byId;

export const getConfig = state => state.config;
export const getCurrentDeck = state => state.decks.byId[state.decks.currentDeckId] || {};
export const getCurrentDeckId = state => state.decks.currentDeckId;
export const getInvites = state => state.invites.byId || {};
export const getCurrentInvite = (state) => state.invites.byId[state.invites.currentInviteId] || {};
export const getUsers = (state) => state.user.byId || {};
export const getCurrentUser = state => state.user.userId && getUsers(state)[state.user.userId] || {};
export const getOtp = state => state.user.otp;
export const getCurrentSlideId = state => state.slides.currentSlideId;
export const getSlides = state => state.slides.byId || {};
export const getSlideIndex = state => state.slides.slideIndex;
export const getDecks = state => state.decks.byId || {};
export const getDeckIndex = state => state.decks.deckIndex;
export const getDeckMetrics = state => state.decks.metricsByDeckId;
export const getFolders = state => state.folders.byId || {};
export const getDialogTemplate = state => state.alerts.dialog && state.alerts.dialog.template;
export const getSearchString = state => state.search.searchText;
export const getSlideRotationIsPaused = state => state.ui.slideRotation.isPaused;

export const getCurrentIds = (state) => {
  const currentInvite = state.invites.byId[state.invites.currentInviteId];

  if (currentInvite) {
    return {
      deck_id: currentInvite.deck_id,
      folder_id: currentInvite.folder_id
    };
  }

  const currentDeck = state.decks.byId[state.decks.currentDeckId];

  if (!currentDeck) {
    return {};
  }

  return {
    deck_id: currentDeck.id,
    folder_id: currentDeck.folder_id
  };
};

export const getComponentById = createSelector(
  getComponents,
  getPropsId,
  (components, id) => components[id] || {}
);

export const getCurrentSlide = createSelector(
  getSlides,
  getCurrentSlideId,
  (slidesById, currentSlideId) => {
    return slidesById[currentSlideId];
  }
);

export const getSlideByComponentId = createSelector(
  getComponentById,
  getSlides,
  (component, slides) => slides[component.slide_id] || {}
);

export const getDeckCount = createSelector(
  getDecks,
  decks => Object.keys(decks).length
);

export const getDeckByComponentId = createSelector(
  getSlideByComponentId,
  getDecks,
  (slide, decks) => decks[slide.deck_id] || {}
);

export const getCurrentFolder = createSelector(
  getCurrentDeck,
  getFolders,
  (deck, foldersById) => foldersById[deck.folder_id]
);

export const getFolderByComponentId = createSelector(
  getDeckByComponentId,
  getFolders,
  (deck, folders) => folders[deck.folder_id] || {}
);

export const getSlideById = createSelector(
  getPropsId,
  getSlides,
  (id, slides) => slides[id] || {}
);

export const getDeckBySlideId = createSelector(
  getSlideById,
  getDecks,
  (slide, decks) => decks[slide.deck_id] || {}
);

export const getFolderBySlideId = createSelector(
  getDeckBySlideId,
  getFolders,
  (deck, folders) => folders[deck.folder_id] || {}
);

export const getFolderByInviteId = createSelector(
  getFolders,
  getCurrentIds,
  (folders, invite) => folders[invite.folder_id] || {}
);

export const getSlidesByCurrentInvite = createSelector(
  getSlides,
  getCurrentIds,
  getDecks,
  (slides, invite, decks) => {
    const deck = decks[invite.deck_id];
    const slideOrder = deck && deck.slide_order || [];

    if (Object.keys(slides).filter(Boolean).length !== slideOrder.length) {
      return [];
    }
    return slideOrder.map(id => slides[id])
      .filter(s => s.deck_id === invite.deck_id);
  }
);

export const getFilteredSlides = createSelector(
  getSlidesByCurrentInvite,
  getSearchString,
  (slides, searchString) => {
    const lcss = searchString.toLowerCase();
    return slides.filter(slide => {
      const slc = slide.name.toLowerCase();
      return slc.includes(lcss);
    });
  }
);

export const getInviteeCount = createSelector(
  getCurrentIds,
  getDeckMetrics,
  (invite, metricsByDeckId) => {
    const metrics = metricsByDeckId[invite.deck_id];
    return metrics && metrics.unique_invited_users;
  }
);

export const getFolderId = createSelector(
  getCurrentIds,
  invite => invite.folder_id
);

export const getInviteeUserId = createSelector(
  getCurrentInvite,
  invite => invite.invitee_user_id
);

export const getUser = createSelector(
  getInviteeUserId,
  getUsers,
  (inviteeUserId, users) => users[inviteeUserId] || {}
);

export const getFolderPermissions = createSelector(
  getUser,
  getFolderId,
  (user, folderId) => user.permissions && user.permissions[folderId] || []
);

export const getEditFolderPermissions = createSelector(
  getFolderPermissions,
  permissions => permissions.some(perm => perm === 'folder:read')
);

export const getFolderOptionsByComponentId = createSelector(
  getFolderByComponentId,
  folder => folder.options || {}
);

const TEMPLATES = ['invite_request_dialog', 'invite_sent_dialog', 'invite_suspended_dialog', 'invite_open_error'];

export const getShouldShowDialogBackground = createSelector(
  getDialogTemplate,
  template => TEMPLATES.some(t => t === template)
);

export const getComponentTypeForId = createSelector(
  (state) => state.components.componentTypes.byId,
  getComponentById,
  (componentTypes, component) => {
    return Object.values(componentTypes).find(componentType =>
      componentType.component_type_id === component.component_type_id) || {};
  }
);

export const getComponentThumbnailUrl = createSelector(
  getComponentTypeForId,
  componentType => componentType.thumbnail_url
);

export const getComponentPictogram = createSelector(
  getComponentTypeForId,
  componentType => componentType.pictogram
);

export const getTheme = createSelector(
  getCurrentFolder,
  (folder) => (folder && folder.options && folder.options.theme)
);
